import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { baseApi } from "../../../services/rtkApi/baseApi";
import { LATE_LOOK_MS } from "../../badges/reveal/BadgeRevealHost";
import { formatUserHandle } from "../../../utils/userHandle";
import { useGetEmojiSelectorQuery } from "../../emojis/api/emojiApi";
import { readGameError, useJoinSeasonMutation } from "../api/gameApi";
import type { JoinResult, Season } from "../api/types";
import StepRules from "./StepRules";
import StepReach from "./StepReach";
import StepConfirm from "./StepConfirm";
import { ordinal } from "../utils/seasonText";

type Step = 1 | 2 | 3;

const STEPS: Array<{ n: Step; label: string }> = [
  { n: 1, label: "The game" },
  { n: 2, label: "Notifications" },
  { n: 3, label: "Join" },
];

const NOT_REACHABLE_TEXT =
  "Turn on notifications for this site or link Telegram first, so the game can reach you.";
const JOIN_FAILED_TEXT = "Could not join right now. Try again in a minute.";

/**
 * How long after a join to ask for badges again. The backend runs badge checks
 * 5 s after the game.joined event, so the season badge exists by then and its
 * reveal shows right after "You're in" instead of on the next minute's poll.
 */
export const BADGE_REFRESH_AFTER_JOIN_MS = LATE_LOOK_MS;

interface Props {
  season: Season;
  /** "fix": step 2 only, for a player already in who can no longer be reached. */
  mode?: "join" | "fix";
  onClose: () => void;
  onJoined?: (result: JoinResult) => void;
}

function StepDots({ step }: { step: Step }) {
  return (
    <ol className="gm-steps" aria-label="Steps">
      {STEPS.map((s) => {
        const state = s.n < step ? "done" : s.n === step ? "current" : "todo";
        return (
          <li key={s.n} data-state={state} aria-current={state === "current" ? "step" : undefined}>
            <span className="gm-steps__num">{s.n}</span>
            <span>{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Joining the game, in three steps: the rules, being reachable (required),
 * then the join itself. Nothing is persisted; closing the panel starts over.
 */
const JoinFlow: React.FC<Props> = ({ season, mode = "join", onClose, onJoined }) => {
  const dispatch = useAppDispatch();
  const username = useAppSelector((s) => s.auth.username) || "";
  const [step, setStep] = useState<Step>(mode === "fix" ? 2 : 1);
  const [reachMessage, setReachMessage] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joined, setJoined] = useState<JoinResult | null>(null);
  const [join, { isLoading: joining }] = useJoinSeasonMutation();
  const inFlight = useRef(false);
  const panelRef = useRef<HTMLElement | null>(null);

  const { data: selector } = useGetEmojiSelectorQuery(undefined, { skip: step !== 3 });
  const handle = formatUserHandle(username, selector?.data?.me?.signatureEmoji ?? null);

  // keep the panel's top in view as the steps change height (phones)
  useEffect(() => {
    panelRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  }, [step, joined]);

  // once in, fetch badges again shortly after so the season badge reveals
  useEffect(() => {
    if (!joined) return undefined;
    const timer = setTimeout(() => {
      dispatch(baseApi.util.invalidateTags(["Badges"]));
    }, BADGE_REFRESH_AFTER_JOIN_MS);
    return () => clearTimeout(timer);
  }, [joined, dispatch]);

  const doJoin = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setJoinError(null);
    try {
      const res = await join(season.id).unwrap();
      setJoined(res);
      onJoined?.(res);
    } catch (err) {
      const e = readGameError(err);
      if (e.code === "not_reachable") {
        setReachMessage(e.message || NOT_REACHABLE_TEXT);
        setStep(2);
      } else {
        setJoinError(e.message || JOIN_FAILED_TEXT);
      }
    } finally {
      inFlight.current = false;
    }
  };

  if (joined) {
    return (
      <section className="gm-join" ref={panelRef} aria-label="Joined">
        <div className="gm-success">
          <p className="gm-success__big">You&apos;re in.</p>
          <p className="gm-lead">
            {joined.rank != null
              ? `You start with ${joined.total} points, in ${ordinal(joined.rank)} place.`
              : "Your points start with the first race."}
          </p>
          <button type="button" className="gm-button" onClick={onClose}>
            See the standings
          </button>
        </div>
      </section>
    );
  }

  if (mode === "fix") {
    return (
      <section className="gm-join" ref={panelRef} aria-label="Notifications">
        <StepReach nextLabel="Done" onNext={onClose} onBack={onClose} />
      </section>
    );
  }

  return (
    <section className="gm-join" ref={panelRef} aria-label="Join the game">
      <StepDots step={step} />
      {step === 1 ? <StepRules season={season} onNext={() => setStep(2)} /> : null}
      {step === 2 ? (
        <StepReach
          nextLabel="Next"
          onNext={() => {
            setReachMessage(null);
            setStep(3);
          }}
          onBack={() => setStep(1)}
          serverMessage={reachMessage}
        />
      ) : null}
      {step === 3 ? (
        <StepConfirm
          season={season}
          handle={handle}
          joining={joining}
          error={joinError}
          onJoin={doJoin}
          onBack={() => setStep(2)}
        />
      ) : null}
      <button type="button" className="gm-link-button" onClick={onClose}>
        Not now
      </button>
    </section>
  );
};

export default JoinFlow;
